import ModuleContext from 'CMFW/context/ModuleContext';
import { ContainerRight, EditorRow, FormContainer } from 'components/common/styles';
import { useForceRender } from 'hooks/useForceRender';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, IconButton, MenuItem, TextField } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import TranslationUnifiedDashboardModel, {
  IUnifiedDashboardCell,
  IUnifiedDialect,
  IUnifiedLanguage,
} from '../../../models/TranslationUnifiedDashboardModel';
import { translationProgressRenderer } from '../../../CMFW/grid/cellRenderers/translationProgressRenderer';
import UnifiedCreateProjectModal from '../modals/UnifiedCreateProjectModal';
import { IRefIds } from '../../../constants/refs';

interface IClickedCell {
  ref: number;
  courseId: number;
  dialectId: number;
}

const UnifiedDashboard: React.FC = () => {
  const forceRender = useForceRender();
  const model = useMemo(() => new TranslationUnifiedDashboardModel({ render: forceRender }), [forceRender]);

  const [dialectId, setDialectId] = useState<number>(0);
  const [languageId, setLanguageId] = useState<number>(0);
  const [clickedCell, setClickedCell] = useState<IClickedCell | null>(null);
  const [courseRanges, setCourseRanges] = useState<{ [courseId: string]: { start: number; end: number } }>({});

  useEffect(() => {
    model.loadDialects();
  }, [model]);

  const dialects = model.getDialects();
  const languages = model.getLanguages();
  const dashboardData = model.getDashboardData();

  const handleDialectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(event.target.value);
    setDialectId(val);
    model.setDialectId(val);
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(event.target.value);
    setLanguageId(val);
    model.setLanguageId(val);
  };

  const handleLoadClick = () => {
    setCourseRanges({});
    model.getDashboard();
  };

  // Sync local ranges when dashboard data arrives
  useEffect(() => {
    if (dashboardData) {
      setCourseRanges((prev) => {
        const ranges: { [courseId: string]: { start: number; end: number } } = {};
        dashboardData.courses.forEach((c) => {
          ranges[String(c.id)] = prev[String(c.id)] || { start: c.startLesson, end: c.endLesson };
        });
        return ranges;
      });
    }
  }, [dashboardData]);

  const handleCourseRangeChange = (cId: number, field: 'start' | 'end', value: number) => {
    setCourseRanges((prev) => ({
      ...prev,
      [String(cId)]: {
        ...prev[String(cId)],
        [field]: value,
      },
    }));
  };

  const handleReloadCourse = (cId: number) => {
    const range = courseRanges[String(cId)];
    if (range) {
      model.reloadCourse(cId, range.start, range.end);
    }
  };

  const handleCellClick = (ref: number, cId: number, cell: IUnifiedDashboardCell) => {
    if (cell.pending > 0) {
      setClickedCell({ ref, courseId: cId, dialectId });
    }
  };

  const sharedRefs = [
    IRefIds.bankKLine,
    IRefIds.bankPanelIntro,
    IRefIds.bankParagraph,
    IRefIds.bankTableLiteral,
    IRefIds.bankPanelTitle,
    IRefIds.bankLessonTitle,
    IRefIds.bankTrainerTitle,
    IRefIds.vocabularyCategories,
    IRefIds.hanziComponentName,
  ];

  // Group dialects by language for display
  const dialectsByLanguage: { [lang: string]: IUnifiedDialect[] } = {};
  dialects.forEach((d) => {
    if (!dialectsByLanguage[d.languageName]) {
      dialectsByLanguage[d.languageName] = [];
    }
    dialectsByLanguage[d.languageName].push(d);
  });

  const canLoad = dialectId > 0 && languageId > 0;

  return (
    <ModuleContext.Provider value={model}>
      <ContainerRight>
        <FormContainerStyled>
          <EditorRowStyled>
            <SelectField
              select
              value={languageId}
              variant="outlined"
              label="Target Language"
              onChange={handleLanguageChange}
              size="small"
            >
              <MenuItem value={0}>Select language...</MenuItem>
              {languages.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name}
                </MenuItem>
              ))}
            </SelectField>
            <SelectField
              select
              value={dialectId}
              variant="outlined"
              label="Source Dialect"
              onChange={handleDialectChange}
              size="small"
            >
              <MenuItem value={0}>Select dialect...</MenuItem>
              {Object.keys(dialectsByLanguage)
                .sort()
                .map((lang) =>
                  dialectsByLanguage[lang].map((d) => (
                    <MenuItem key={d.dialectId} value={d.dialectId}>
                      {d.languageName} - {d.dialectName} ({d.dialectCode})
                    </MenuItem>
                  )),
                )}
            </SelectField>
            <Button variant="contained" size="small" onClick={handleLoadClick} disabled={!canLoad || model.loadingGrid}>
              {model.loadingGrid ? <CircularProgress size={16} /> : 'load'}
            </Button>
          </EditorRowStyled>

          {model.loadingGrid && !dashboardData && (
            <LoadingContainer>
              <CircularProgress size={32} />
            </LoadingContainer>
          )}

          {dashboardData && (() => {
            const courseRefs = dashboardData.refs.filter((r) => !sharedRefs.includes(r.ref));
            const sharedRefsList = dashboardData.refs.filter((r) => sharedRefs.includes(r.ref));

            return (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th>Ref Type</Th>
                      {dashboardData.courses.map((course) => (
                        <ThCourse key={course.id}>
                          <CourseHeaderName>{course.name}</CourseHeaderName>
                          <CourseHeaderRange>
                            <RangeInput
                              type="number"
                              value={courseRanges[String(course.id)]?.start ?? course.startLesson}
                              onChange={(e) =>
                                handleCourseRangeChange(course.id, 'start', parseInt(e.target.value) || 1)
                              }
                            />
                            <RangeSeparator>-</RangeSeparator>
                            <RangeInput
                              type="number"
                              value={courseRanges[String(course.id)]?.end ?? course.endLesson}
                              onChange={(e) =>
                                handleCourseRangeChange(course.id, 'end', parseInt(e.target.value) || 1)
                              }
                            />
                            <ReloadBtn
                              size="small"
                              onClick={() => handleReloadCourse(course.id)}
                              title="Reload this course"
                              disabled={model.isCourseLoading(course.id)}
                            >
                              {model.isCourseLoading(course.id) ? (
                                <CircularProgress size={12} />
                              ) : (
                                <FontAwesomeIcon icon={faSyncAlt} size="xs" />
                              )}
                            </ReloadBtn>
                          </CourseHeaderRange>
                        </ThCourse>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {courseRefs.map((ref, idx) => (
                      <Tr key={ref.ref} $odd={idx % 2 === 1}>
                        <TdRefName $isBank={false}>{ref.refName}</TdRefName>
                        {dashboardData.courses.map((course) => {
                          const isLoading = model.isCourseLoading(course.id);
                          const cell = dashboardData.data[ref.ref]?.[course.id] || { pending: 0, doing: 0, done: 0 };
                          const total = cell.pending + cell.doing + cell.done;
                          return (
                            <Td
                              key={course.id}
                              $clickable={!isLoading && cell.pending > 0}
                              $loading={isLoading}
                              onClick={() => !isLoading && handleCellClick(ref.ref, course.id, cell)}
                            >
                              {isLoading ? (
                                <LoadingCell />
                              ) : (
                                total > 0 && (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: translationProgressRenderer({ value: { ...cell, dialect: dialectId } }),
                                    }}
                                  />
                                )
                              )}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </tbody>
                </Table>

                {sharedRefsList.length > 0 && (
                  <>
                    <SectionTitle>Shared (not course-dependent)</SectionTitle>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Ref Type</Th>
                          <Th>Status</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {sharedRefsList.map((ref, idx) => {
                          const firstCourse = dashboardData.courses[0];
                          const totals = firstCourse
                            ? dashboardData.data[ref.ref]?.[firstCourse.id] || { pending: 0, doing: 0, done: 0 }
                            : { pending: 0, doing: 0, done: 0 };
                          const total = totals.pending + totals.doing + totals.done;
                          return (
                            <Tr key={ref.ref} $odd={idx % 2 === 1}>
                              <TdRefName $isBank={true}>{ref.refName}</TdRefName>
                              <Td $clickable={false} $loading={false}>
                                {total > 0 && (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: translationProgressRenderer({ value: { ...totals, dialect: dialectId } }),
                                    }}
                                  />
                                )}
                              </Td>
                            </Tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </>
                )}
              </TableWrapper>
            );
          })()}
        </FormContainerStyled>
      </ContainerRight>
      {clickedCell && (
        <UnifiedCreateProjectModal
          onClose={() => {
            setClickedCell(null);
            const range = courseRanges[String(clickedCell.courseId)];
            if (range) {
              model.reloadCourse(clickedCell.courseId, range.start, range.end);
            }
          }}
          courseId={clickedCell.courseId}
          refId={clickedCell.ref}
          startLesson={courseRanges[String(clickedCell.courseId)]?.start ?? 1}
          endLesson={courseRanges[String(clickedCell.courseId)]?.end ?? 1000}
          dialectId={clickedCell.dialectId}
        />
      )}
    </ModuleContext.Provider>
  );
};

export default UnifiedDashboard;

const EditorRowStyled = styled(EditorRow)`
  justify-content: flex-start;
  gap: 10px;
`;

const FormContainerStyled = styled(FormContainer)`
  max-width: initial;
  height: auto;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SelectField = styled(TextField)`
  width: 240px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const TableWrapper = styled.div`
  overflow: auto;
  flex: 1;
  margin-top: 10px;
`;

const Table = styled.table`
  border-collapse: collapse;
`;

const Tr = styled.tr<{ $odd: boolean }>`
  background: ${(props) => (props.$odd ? '#f9f9f9' : 'white')};
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 6px 10px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: top;
`;

const ThCourse = styled.th`
  position: sticky;
  top: 0;
  background: #e3f2fd;
  border: 1px solid #ddd;
  padding: 4px 6px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: top;
  width: 150px;
`;

const CourseHeaderName = styled.div`
  margin-bottom: 4px;
`;

const CourseHeaderRange = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const RangeInput = styled.input`
  width: 42px;
  padding: 1px 3px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 3px;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const RangeSeparator = styled.span`
  color: #999;
`;

const ReloadBtn = styled(IconButton)`
  && {
    padding: 2px;
    margin-left: 2px;
  }
`;

const SectionTitle = styled.div`
  margin-top: 16px;
  margin-bottom: 4px;
  font-weight: 600;
  color: #555;
`;

const TdRefName = styled.td<{ $isBank: boolean }>`
  border: 1px solid #ddd;
  padding: 4px 10px;
  font-weight: 500;
  white-space: nowrap;
  width: 150px;
  background: ${(props) => (props.$isBank ? '#fff8e1' : 'white')};
`;

const Td = styled.td<{ $clickable: boolean; $loading: boolean }>`
  border: 1px solid #ddd;
  padding: 2px 4px;
  text-align: center;
  width: 150px;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  opacity: ${(props) => (props.$loading ? 0.4 : 1)};
  &:hover {
    background: ${(props) => (props.$clickable ? '#e3f2fd' : 'inherit')};
  }
`;

const LoadingCell = styled.div`
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 3px;
  margin: 2px 4px;
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
